import type { DoctorAvailabilityGroup } from 'features/Schedule/api/schedule.types'
import { toSiteWallClockDate } from 'common/services/dateService'

export type AvailabilityStatus = 'in_office' | 'out_of_office' | 'lunch' | 'reserved' | 'unavailable'

export type AvailabilityBlock = {
  status: AvailabilityStatus
  start: number
  end: number
}

const DAY_SECONDS = 24 * 60 * 60

const STATUS_CLASS: Record<AvailabilityStatus, string> = {
  in_office: 'schedule-availability-in',
  out_of_office: 'schedule-availability-out',
  lunch: 'schedule-availability-lunch',
  reserved: 'schedule-availability-reserved',
  unavailable: 'schedule-availability-none',
}

const STATUS_LABEL: Partial<Record<AvailabilityStatus, string>> = {
  out_of_office: 'OUT',
  lunch: 'LUNCH',
  reserved: 'RESERVED',
}

const toSeconds = (value: string): number => {
  const [hours = 0, minutes = 0, seconds = 0] = value.split(':').map((part) => Number(part))
  return hours * 3600 + minutes * 60 + seconds
}

export const dateToDaySeconds = (date: Date): number =>
  date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds()

const rangesOverlap = (start: number, end: number, itemStart: number, itemEnd: number): boolean =>
  start < itemEnd && end > itemStart

const itemsFor = (groups: DoctorAvailabilityGroup[], status: string, userId: number) =>
  groups
    .find((group) => group.status === status)
    ?.items.filter((item) => item.user_id === userId) ?? []

export const userHasAvailability = (
  groups: DoctorAvailabilityGroup[] | undefined,
  userId: number,
): boolean => !!groups?.some((group) => group.items.some((item) => item.user_id === userId))

export const getUserStatusAt = (
  groups: DoctorAvailabilityGroup[] | undefined,
  userId: number,
  seconds: number,
): AvailabilityStatus | null => {
  if (!groups?.length || !userId) return null

  const covers = (status: string) =>
    itemsFor(groups, status, userId).some(
      (item) => seconds >= toSeconds(item.start) && seconds < toSeconds(item.end),
    )

  if (covers('out_of_office')) return 'out_of_office'
  if (covers('lunch')) return 'lunch'
  if (covers('reserved')) return 'reserved'
  if (covers('in_office')) return 'in_office'
  return null
}

/** In office wins when anyone selected is in. Reserved, lunch, and out show when nobody selected is in. */
export const getDisplayStatusAt = (
  groups: DoctorAvailabilityGroup[] | undefined,
  userIds: number[],
  seconds: number,
): AvailabilityStatus | null => {
  const statuses = userIds
    .map((userId) => getUserStatusAt(groups, userId, seconds))
    .filter((status): status is AvailabilityStatus => !!status)

  if (!statuses.length) return null
  if (statuses.some((status) => status === 'in_office')) return 'in_office'
  if (statuses.some((status) => status === 'lunch')) return 'lunch'
  if (statuses.some((status) => status === 'reserved')) return 'reserved'
  if (statuses.some((status) => status === 'out_of_office')) return 'out_of_office'
  return null
}

const mergeBlocks = (blocks: AvailabilityBlock[]): AvailabilityBlock[] => {
  const merged: AvailabilityBlock[] = []
  for (const block of blocks) {
    const previous = merged[merged.length - 1]
    if (previous && previous.status === block.status && previous.end === block.start) {
      previous.end = block.end
      continue
    }
    merged.push({ ...block })
  }
  return merged
}

export const buildDisplayBlocks = (
  groups: DoctorAvailabilityGroup[] | undefined,
  userIds: number[],
): AvailabilityBlock[] => {
  if (!userIds.length) return []
  if (userIds.length === 1 && !userHasAvailability(groups, userIds[0])) {
    return [{ status: 'unavailable', start: 0, end: DAY_SECONDS }]
  }
  if (!groups?.length) return []

  const boundaries = new Set<number>([0, DAY_SECONDS])
  for (const group of groups) {
    for (const item of group.items) {
      if (!userIds.includes(item.user_id)) continue
      boundaries.add(toSeconds(item.start))
      boundaries.add(toSeconds(item.end))
    }
  }

  const points = [...boundaries].sort((left, right) => left - right)
  const raw: AvailabilityBlock[] = []
  for (let index = 0; index < points.length - 1; index++) {
    const status = getDisplayStatusAt(groups, userIds, points[index]) ?? 'unavailable'
    raw.push({ status, start: points[index], end: points[index + 1] })
  }
  return mergeBlocks(raw)
}

const normalizeRange = (
  start: Date,
  end: Date,
  timeZone?: string,
): { start: number; end: number } | null => {
  const toSecondsOfDay = (date: Date) => {
    const wall = timeZone ? toSiteWallClockDate(date, timeZone) : date
    return dateToDaySeconds(wall ?? date)
  }
  const startSeconds = toSecondsOfDay(start)
  let endSeconds = toSecondsOfDay(end)
  if (endSeconds <= startSeconds) endSeconds = DAY_SECONDS
  if (endSeconds <= startSeconds) return null
  return { start: startSeconds, end: endSeconds }
}

export const isUserRangeUnavailable = (
  groups: DoctorAvailabilityGroup[] | undefined,
  userId: number,
  start: Date,
  end: Date,
): boolean => {
  if (!userHasAvailability(groups, userId)) return false
  const range = normalizeRange(start, end)
  if (!range || !groups) return false

  return (['out_of_office', 'lunch', 'reserved'] as const).some((status) =>
    itemsFor(groups, status, userId).some((item) =>
      rangesOverlap(range.start, range.end, toSeconds(item.start), toSeconds(item.end)),
    ),
  )
}

export const isSelectionRangeUnavailable = (
  groups: DoctorAvailabilityGroup[] | undefined,
  userIds: number[],
  start: Date,
  end: Date,
  timeZone?: string,
): boolean => {
  if (!userIds.length) return false
  const range = normalizeRange(start, end, timeZone)
  if (!range) return false

  return buildDisplayBlocks(groups, userIds).some(
    (block) =>
      block.status !== 'in_office' && rangesOverlap(range.start, range.end, block.start, block.end),
  )
}

export const availabilityClassName = (status: AvailabilityStatus): string => STATUS_CLASS[status]

export const availabilityLabelForCell = (
  blocks: AvailabilityBlock[],
  cellStart: Date,
  slotMinutes: number,
): string | null => {
  const start = dateToDaySeconds(cellStart)
  const end = start + slotMinutes * 60
  const block = blocks.find(
    (item) =>
      item.status !== 'in_office' &&
      item.status !== 'unavailable' &&
      start < item.end &&
      end > item.start,
  )
  if (!block) return null

  if (block.start < start || block.start >= end) return null
  return STATUS_LABEL[block.status] ?? null
}

export const statusForCell = (
  blocks: AvailabilityBlock[],
  cellStart: Date,
): AvailabilityStatus | null => {
  const seconds = dateToDaySeconds(cellStart)
  return blocks.find((block) => seconds >= block.start && seconds < block.end)?.status ?? null
}

const STATUS_CLASSES = Object.values(STATUS_CLASS)

// Paints one Syncfusion work cell with the doctor's status; used by the Find Availability preview.
export const paintAvailabilityCell = (
  element: HTMLElement,
  blocks: AvailabilityBlock[],
  cellStart: Date,
  slotMinutes: number,
): void => {
  element.classList.remove(...STATUS_CLASSES)
  delete element.dataset.availabilityLabel
  if (element.classList.contains('e-disabled-cell')) return

  const status = statusForCell(blocks, cellStart)
  if (!status) return

  element.classList.add(availabilityClassName(status))
  const label = availabilityLabelForCell(blocks, cellStart, slotMinutes)
  if (label) element.dataset.availabilityLabel = label
}
